import { useEffect, useMemo, useState } from 'react';

import { RunScopeFolderTree } from '../../components/screens/RunScopeScreen';
import type {
  GitHubRepositoryBranchSummary,
  GitHubRepositoryDirectorySummary,
  Workspace,
  WorkspaceScope
} from '../../components/screens/RunScreens';
import { runFormatNumber } from '../../components/screens/helpers/runScreenTypes';
import {
  compactScopePaths,
  formatFileSize,
  getCodebaseZipValidationError,
  type LoadState
} from '../../components/workflowShared';
import { AppConnectGitHubWidget, AppUploadZipButton } from './AppChatWidgets';
import type { AppWorkspaceResourcePointer } from './appChatStore';
import {
  loadBranches,
  loadDirectories,
  loadUploadedCodebaseDetails,
  type AppRepositoryChoice,
  type AppUploadedCodebaseDetails
} from './appScanApi';
import { IconArchive, IconBranch, IconGithub } from './appIcons';
import type { AppGitHubState } from './useAppAccount';

interface ScopeDraft {
  installationId: number | null;
  repositoryId: number | null;
  branch: string;
  scopePaths: string[];
  scanType: 'smart-contract' | 'web-api';
  assumptionsFilePath: string;
}

interface AppScopeViewProps {
  workspace: Workspace;
  githubState: AppGitHubState;
  sessionId: string;
  choices: AppRepositoryChoice[];
  choicesState: LoadState;
  /** ZIP codebases uploaded to this workspace, newest first. */
  uploadedCodebases: AppWorkspaceResourcePointer[];
  onConnectGitHub: () => void;
  onManageGitHub: () => void;
  /** Starts a pentest of an uploaded codebase, without GitHub access. */
  onUploadZip: (file: File) => void;
  onSave: (scope: WorkspaceScope) => Promise<void>;
}

const toDraft = (scope?: WorkspaceScope): ScopeDraft => ({
  installationId: scope?.installationId ?? null,
  repositoryId: scope?.repositoryId ?? null,
  branch: scope?.branch ?? '',
  scopePaths: compactScopePaths(scope?.scopePaths ?? []),
  scanType: scope?.scanType ?? 'smart-contract',
  assumptionsFilePath: scope?.assumptionsFilePath ?? ''
});

const toChoiceValue = (installationId: number | null, repositoryId: number | null): string => {
  return installationId && repositoryId ? `${String(installationId)}:${String(repositoryId)}` : '';
};

const formatCount = (count: number, noun: string): string => {
  return `${runFormatNumber(count)} ${noun}${count === 1 ? '' : 's'}`;
};

/** An uploaded ZIP shown in the same rows as a repository: what it is, what it holds, and its folders. */
function AppUploadedCodebaseFields({ uploads }: { uploads: AppWorkspaceResourcePointer[] }) {
  const [selectedId, setSelectedId] = useState('');
  const [details, setDetails] = useState<AppUploadedCodebaseDetails | null>(null);
  const [detailsState, setDetailsState] = useState<LoadState>('idle');
  const selected = uploads.find((upload) => upload.id === selectedId) ?? uploads[0];
  const selectedResourceId = selected?.id ?? '';
  const selectedWorkspaceId = selected?.workspaceId ?? '';
  const selectedName = selected?.name ?? '';

  useEffect(() => {
    if (!selectedResourceId) {
      setDetails(null);
      setDetailsState('idle');
      return undefined;
    }

    const controller = new AbortController();

    setDetails(null);
    setDetailsState('loading');
    loadUploadedCodebaseDetails(
      { id: selectedResourceId, workspaceId: selectedWorkspaceId, name: selectedName },
      controller.signal
    )
      .then((loaded) => {
        setDetails(loaded);
        setDetailsState('ready');
      })
      .catch((error: unknown) => {
        if (error instanceof DOMException && error.name === 'AbortError') {
          return;
        }

        console.error('Failed to load the uploaded codebase:', error);
        setDetailsState('error');
      });

    return () => controller.abort();
  }, [selectedName, selectedResourceId, selectedWorkspaceId]);

  if (!selected) {
    return null;
  }

  const contents = details?.contents;

  return (
    <>
      <section className="app-scope-field">
        <span className="app-scope-label">Codebase</span>
        <div className="app-scope-control">
          {uploads.length > 1 ? (
            <label className="scope-branch-select app-scope-select">
              <IconArchive aria-hidden={true} />
              <select
                value={selected.id}
                aria-label="Uploaded codebase"
                onChange={(event) => setSelectedId(event.target.value)}
              >
                {uploads.map((upload) => (
                  <option key={upload.id} value={upload.id}>{upload.name}</option>
                ))}
              </select>
            </label>
          ) : (
            <span className="app-scope-value">
              <IconArchive aria-hidden={true} />
              <span>{selected.name}</span>
            </span>
          )}
          {details && (
            <p className="app-widget-muted">
              {`ZIP · ${formatFileSize(details.archiveSizeBytes)} · Uploaded ${new Date(details.uploadedAt).toLocaleDateString(undefined, {
                day: 'numeric',
                month: 'short',
                year: 'numeric'
              })}`}
            </p>
          )}
          {selected.state !== 'ready' && <p className="app-widget-muted">{`Status: ${selected.state}`}</p>}
        </div>
      </section>

      {detailsState === 'loading' && (
        <section className="app-scope-field">
          <span className="app-scope-label">Contents</span>
          <div className="app-skeleton" role="status" aria-label="Loading codebase details"><span /></div>
        </section>
      )}

      {detailsState === 'error' && (
        <section className="app-scope-field">
          <span className="app-scope-label">Contents</span>
          <p className="app-widget-error">The codebase details could not be loaded.</p>
        </section>
      )}

      {detailsState === 'ready' && !contents && (
        <section className="app-scope-field">
          <span className="app-scope-label">Contents</span>
          <p className="app-widget-muted">File details are not available for this older upload.</p>
        </section>
      )}

      {contents && (
        <>
          <section className="app-scope-field">
            <span className="app-scope-label">Contents</span>
            <p className="app-scope-text">
              {[
                formatCount(contents.fileCount, 'file'),
                formatCount(contents.directoryCount, 'folder'),
                `${formatFileSize(contents.totalBytes)} unpacked`
              ].join(' · ')}
            </p>
          </section>

          <section className="app-scope-field app-scope-folders">
            <span className="app-scope-label">Folders</span>
            <RunScopeFolderTree
              githubTreeState="ready"
              githubTreeError=""
              directories={contents.directories}
              scopePaths={[]}
              readOnly={true}
              rootLabel="Codebase root"
            />
          </section>
        </>
      )}

      {details?.context && (
        <section className="app-scope-field">
          <span className="app-scope-label">Context</span>
          <p className="app-scope-text app-scope-context">{details.context}</p>
        </section>
      )}
    </>
  );
}

/** The workspace's scope: what the next scan tests. Laid out like the run scope page, as one quiet document. */
function AppScopeView({
  workspace,
  githubState,
  sessionId,
  choices,
  choicesState,
  uploadedCodebases,
  onConnectGitHub,
  onManageGitHub,
  onUploadZip,
  onSave
}: AppScopeViewProps) {
  const savedKey = JSON.stringify(workspace.scope ?? null);
  const [draft, setDraft] = useState<ScopeDraft>(() => toDraft(workspace.scope));
  const [branches, setBranches] = useState<GitHubRepositoryBranchSummary[]>([]);
  const [branchesState, setBranchesState] = useState<LoadState>('idle');
  const [directories, setDirectories] = useState<GitHubRepositoryDirectorySummary[]>([]);
  const [treeState, setTreeState] = useState<LoadState>('idle');
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [zipError, setZipError] = useState('');
  const { installationId, repositoryId, branch } = draft;
  const selectedChoice = choices.find((choice) => {
    return choice.installationId === installationId && choice.repository.id === repositoryId;
  });

  // Start over from the saved scope when switching workspaces or after saving.
  useEffect(() => {
    setDraft(toDraft(workspace.scope));
    setSaveError('');
    setZipError('');
    // eslint-disable-next-line react-hooks/exhaustive-deps -- savedKey stands for workspace.scope
  }, [workspace.id, savedKey]);

  useEffect(() => {
    if (!installationId || !repositoryId || githubState !== 'connected') {
      setBranches([]);
      setBranchesState('idle');
      return undefined;
    }

    let cancelled = false;

    setBranchesState('loading');
    loadBranches(sessionId, installationId, repositoryId)
      .then((loaded) => {
        if (cancelled) {
          return;
        }

        setBranches(loaded);
        setBranchesState('ready');
        // A newly chosen repository starts on its default branch.
        setDraft((current) => current.branch
          ? current
          : { ...current, branch: loaded.find((entry) => entry.default)?.name ?? loaded[0]?.name ?? '' });
      })
      .catch((error: unknown) => {
        console.error('Failed to load branches:', error);

        if (!cancelled) {
          setBranchesState('error');
        }
      });

    return () => {
      cancelled = true;
    };
  }, [githubState, installationId, repositoryId, sessionId]);

  useEffect(() => {
    if (!installationId || !repositoryId || !branch || githubState !== 'connected') {
      setDirectories([]);
      setTreeState('idle');
      return undefined;
    }

    const controller = new AbortController();

    setTreeState('loading');
    loadDirectories(sessionId, installationId, repositoryId, branch, controller.signal)
      .then((loaded) => {
        const available = new Set(loaded.map((directory) => directory.path));

        setDirectories(loaded);
        setTreeState('ready');
        // Folders that do not exist on this branch cannot stay in scope.
        setDraft((current) => {
          const scopePaths = current.scopePaths.filter((path) => available.has(path));

          return scopePaths.length === current.scopePaths.length ? current : { ...current, scopePaths };
        });
      })
      .catch((error: unknown) => {
        if (error instanceof DOMException && error.name === 'AbortError') {
          return;
        }

        console.error('Failed to load folders:', error);
        setTreeState('error');
      });

    return () => controller.abort();
  }, [branch, githubState, installationId, repositoryId, sessionId]);

  const isDirty = useMemo(() => {
    return JSON.stringify({ ...draft, assumptionsFilePath: draft.assumptionsFilePath.trim() })
      !== JSON.stringify(toDraft(workspace.scope));
  }, [draft, workspace.scope]);
  const canSave = isDirty && Boolean(installationId && repositoryId && branch) && !isSaving;
  const commit = branches.find((entry) => entry.name === branch)?.commitSha?.slice(0, 7);

  const selectRepository = (value: string) => {
    const [nextInstallationId, nextRepositoryId] = value.split(':').map(Number);

    setDraft((current) => ({
      ...current,
      installationId: nextInstallationId || null,
      repositoryId: nextRepositoryId || null,
      branch: '',
      scopePaths: []
    }));
  };

  const toggleScopePath = (path: string) => {
    setDraft((current) => {
      if (current.scopePaths.includes(path)) {
        return { ...current, scopePaths: current.scopePaths.filter((entry) => entry !== path) };
      }

      // Choosing a subfolder of a selected folder narrows the selection to the subfolder.
      const withoutAncestors = current.scopePaths.filter((entry) => !path.startsWith(`${entry}/`));

      return { ...current, scopePaths: compactScopePaths([...withoutAncestors, path]) };
    });
  };

  const save = () => {
    if (!installationId || !repositoryId || !branch) {
      return;
    }

    const assumptionsFilePath = draft.assumptionsFilePath.trim();

    setIsSaving(true);
    setSaveError('');
    onSave({
      installationId,
      repositoryId,
      ...(selectedChoice ? {
        owner: selectedChoice.repository.owner,
        repo: selectedChoice.repository.repo
      } : {}),
      branch,
      scopePaths: compactScopePaths(draft.scopePaths),
      scanType: draft.scanType,
      ...(assumptionsFilePath ? { assumptionsFilePath } : {})
    })
      .catch((error: unknown) => {
        console.error('Failed to save scope:', error);
        setSaveError('The scope could not be saved.');
      })
      .finally(() => setIsSaving(false));
  };

  const uploadZip = (file: File) => {
    const validationError = getCodebaseZipValidationError(file);

    setZipError(validationError);

    if (!validationError) {
      onUploadZip(file);
    }
  };

  // A workspace tests either a GitHub repository or uploaded ZIPs, never both; the backend refuses mixing them.
  if (!workspace.scope && uploadedCodebases.length > 0) {
    return (
      <div className="app-scroll">
        <div className="app-scope">
          <AppUploadedCodebaseFields uploads={uploadedCodebases} />

          <section className="app-scope-field">
            <span className="app-scope-label">ZIP upload</span>
            <div className="app-scope-control">
              <p className="app-widget-muted">
                Upload a newer snapshot of your codebase. Use a different workspace to test a GitHub repository.
              </p>
              <div>
                <AppUploadZipButton onSelect={uploadZip} />
              </div>
              {zipError && <p className="app-widget-error" role="alert">{zipError}</p>}
            </div>
          </section>
        </div>
      </div>
    );
  }

  if (githubState !== 'connected') {
    return (
      <div className="app-scroll">
        <div className="app-scope app-scope-empty">
          <p>
            {workspace.scope
              ? 'Connect GitHub to change the repository Havu tests.'
              : 'Connect GitHub to choose what Havu tests, or upload a ZIP of your codebase to assess it without repository access.'}
          </p>
          <AppConnectGitHubWidget
            githubState={githubState}
            onConnect={onConnectGitHub}
            onManage={onManageGitHub}
            onUploadZip={workspace.scope ? undefined : uploadZip}
          />
          {zipError && <p className="app-widget-error" role="alert">{zipError}</p>}
        </div>
      </div>
    );
  }

  return (
    <div className="app-scope-page">
      <div className="app-scroll">
        <div className="app-scope">
          <section className="app-scope-field">
            <span className="app-scope-label">Repository</span>
            {choicesState === 'loading' ? (
              <div className="app-skeleton" role="status" aria-label="Loading repositories"><span /></div>
            ) : (
              <label className="scope-branch-select app-scope-select">
                <IconGithub aria-hidden={true} />
                <select
                  value={toChoiceValue(installationId, repositoryId)}
                  aria-label="Repository"
                  onChange={(event) => selectRepository(event.target.value)}
                >
                  {!selectedChoice && (
                    <option value={toChoiceValue(installationId, repositoryId)} disabled>
                      {repositoryId ? 'Repository no longer accessible' : 'Choose a repository'}
                    </option>
                  )}
                  {choices.map((choice) => (
                    <option
                      key={`${String(choice.installationId)}:${String(choice.repository.id)}`}
                      value={toChoiceValue(choice.installationId, choice.repository.id)}
                    >
                      {choice.repository.fullName}
                    </option>
                  ))}
                </select>
              </label>
            )}
          </section>

          {selectedChoice && (
            <>
              <section className="app-scope-field">
                <span className="app-scope-label">Scan type</span>
                <label className="scope-branch-select app-scope-select">
                  <select
                    value={draft.scanType}
                    aria-label="Scan type"
                    onChange={(event) => setDraft((current) => ({
                      ...current,
                      scanType: event.target.value as ScopeDraft['scanType']
                    }))}
                  >
                    <option value="smart-contract">Smart contracts</option>
                    <option value="web-api">Web and API source</option>
                  </select>
                </label>
              </section>

              <section className="app-scope-field">
                <span className="app-scope-label">Branch</span>
                <div className="app-scope-control">
                  <div className="scope-branch-row">
                    <label className="scope-branch-select app-scope-select">
                      <IconBranch aria-hidden={true} />
                      <select
                        value={branch}
                        aria-label="Branch"
                        disabled={branchesState !== 'ready' || branches.length === 0}
                        onChange={(event) => setDraft((current) => ({ ...current, branch: event.target.value, scopePaths: [] }))}
                      >
                        {branches.length === 0 && <option value={branch}>{branch || 'Loading…'}</option>}
                        {branches.map((entry) => (
                          <option key={entry.name} value={entry.name}>
                            {entry.default ? `${entry.name} (default)` : entry.name}
                          </option>
                        ))}
                      </select>
                    </label>
                    {commit && <span className="scope-commit-line">{commit}</span>}
                  </div>
                  {branchesState === 'error' && <p className="app-widget-error">Branches could not be loaded.</p>}
                </div>
              </section>

              <section className="app-scope-field app-scope-folders">
                <span className="app-scope-label">Folders</span>
                <RunScopeFolderTree
                  githubTreeState={treeState}
                  githubTreeError="Folders could not be loaded."
                  directories={directories}
                  scopePaths={draft.scopePaths}
                  onSelectRepositoryRoot={() => setDraft((current) => ({ ...current, scopePaths: [] }))}
                  onToggleScopePath={toggleScopePath}
                />
              </section>

              <label className="scope-assumptions-field app-scope-field">
                <span>Assumptions file</span>
                <input
                  type="text"
                  value={draft.assumptionsFilePath}
                  placeholder="docs/assumptions.md"
                  onChange={(event) => setDraft((current) => ({ ...current, assumptionsFilePath: event.target.value }))}
                />
              </label>
            </>
          )}

          {!repositoryId && (
            <section className="app-scope-field">
              <span className="app-scope-label">ZIP upload</span>
              <div className="app-scope-control">
                <p className="app-widget-muted">Or assess an uploaded snapshot of your codebase without GitHub access.</p>
                <div>
                  <AppUploadZipButton onSelect={uploadZip} />
                </div>
                {zipError && <p className="app-widget-error" role="alert">{zipError}</p>}
              </div>
            </section>
          )}
        </div>
      </div>

      {(isDirty || saveError) && (
        <div className="app-scope-bar" role="region" aria-label="Unsaved scope changes">
          <span className={saveError ? 'app-widget-error' : undefined}>{saveError || 'Applies to the next scan.'}</span>
          <button
            type="button"
            className="app-action-button"
            disabled={isSaving}
            onClick={() => {
              setDraft(toDraft(workspace.scope));
              setSaveError('');
            }}
          >
            Discard
          </button>
          <button type="button" className="app-plan-run primary" disabled={!canSave} onClick={save}>
            {isSaving ? <span className="app-spinner" aria-hidden={true} /> : 'Save'}
          </button>
        </div>
      )}
    </div>
  );
}

export default AppScopeView;
